import { 
    Box, 
    Grid, 
    GridSize, 
    styled, 
    SxProps, 
    Theme, 
    Typography
} from "@mui/material";

const RowItem = styled(Grid)(({ theme }) => ({
    borderColor: theme.palette.divider,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',    
    "&>*:not(:first-child)": {
        borderColor: theme.palette.divider,
        borderTopWidth: 1,
        borderTopStyle: 'solid'
    },
    [theme.breakpoints.up('md')]: {
        "&>*:not(:first-child)": {
            borderColor: theme.palette.divider,
            borderLeftWidth: 1,
            borderLeftStyle: 'solid',
            borderTopWidth: 0
        }
    }
}));

interface IKlasisListCol {
    title?: string;
    children?: JSX.Element | JSX.Element[] | string;
    sx?: SxProps<Theme>;
}

export interface IKlasisListRow {
    cols: IKlasisListCol[];
}

interface IKlasisListProps {
    rows: IKlasisListRow[];
    sx?: SxProps<Theme>;
    columns?: any;
}

const KlasisList: React.FC<IKlasisListProps> = (props: IKlasisListProps) => {
    const getColWidth = (index: number, columnCount: number): GridSize => {
        if (columnCount === 1) return 12;

        const isLastColumn = columnCount - 1 === index;
        const singleColumnWidth = Math.floor(12 / columnCount);

        return isLastColumn ? 12 - singleColumnWidth * (columnCount - 1) : singleColumnWidth;
    }

    const renderCol = (
        col: IKlasisListCol, 
        row: IKlasisListRow, 
        colIndex: number, 
        rowIndex: number
    ): JSX.Element => {
        if (!col.children)
            return <></>;

        return (
            <Grid 
                item
                xs={1} 
                sm={1} 
                md={getColWidth(colIndex, row.cols.length)} 
                key={`data-item${rowIndex}-${colIndex}`}
            >
                <KlasisListColumn title={col.title}>
                    {typeof col.children === "string" ?
                        <Typography>{col.children}</Typography> :
                        col.children}
                </KlasisListColumn>
            </Grid>
        );
    };

    const renderRow = (row: IKlasisListRow, rowIndex: number): JSX.Element => {
        if (!row.cols.some(c => c.children))
            return <></>;

        return (
            <RowItem 
                key={`data-row${rowIndex}`}
                container
                columns={{
                    xs: 1,
                    sm: 1,
                    md: 12,
                    ...props.columns
                }}
            >
                {row.cols.map((col, index) => renderCol(col, row, index, rowIndex))}
            </RowItem>
        );
    };
    
    return (
        <Box 
            alignItems="flex-end" 
            flexGrow={1}
            sx={props.sx}
        >
            {props.rows.map((row, rowIndex) => renderRow(row, rowIndex))}
        </Box>
    );
}

interface IClassifierGridColumnProps {
    title?: string;
    children?: JSX.Element | JSX.Element[] | string;
}

const KlasisListColumn: React.FC<IClassifierGridColumnProps> = 
    (props: IClassifierGridColumnProps) => {
    return (
        <>
            <Box p={2}>
                {props.title && (
                    <Typography variant="caption">
                        {props.title}
                    </Typography>
                )}
                <Box
                    marginTop={1}
                    color="text.secondary"
                    sx={{overflowWrap: 'anywhere'}}
                >
                    {props.children}
                </Box>
            </Box>
        </>
    );
}

export default KlasisList;